import { useState } from 'preact/hooks'
import type { AssetQaFindingDto } from '@/types/assetQa'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { TextBox } from '@/components/common/TextBox'

interface Props {
    finding: AssetQaFindingDto
    /**
     * How many open findings this one reason will dismiss. One for an ordinary row; the whole
     * folded group for a repeated one (ORB-967).
     */
    covers: number
    saving: boolean
    /** Shown in place of closing, so a failed save does not look like a silent refusal. */
    error: string | null
    onCancel: () => void
    onConfirm: (reason: string) => void
}

const REASON_MAX_LENGTH = 500

/**
 * Collects the reason before a finding can be dismissed.
 *
 * The reason is required, and the confirm button stays disabled until there is one — the API
 * refuses a blank as well, but a form that lets you press the button and then rejects you teaches
 * people to treat the field as decoration. It is the only thing that makes the record readable
 * later: whether the check was wrong, or the asset is an accepted exception, and those want
 * opposite responses from whoever tunes the rulebook.
 */
export function AssetQaOverrideModal({ finding, covers, saving, error, onCancel, onConfirm }: Props) {
    const [reason, setReason] = useState('')
    const trimmed = reason.trim()

    // ⚑ The count is in the title and the button, not only in the body text. Dismissing 43 findings
    // and dismissing one are different decisions, and the number is the whole difference between
    // them — it does not belong somewhere a reader might skim past on the way to the field.
    const many = covers > 1

    return (
        <ConfirmationModal
            isOpen
            type="warning"
            title={many ? `Override all ${covers} findings?` : 'Override this finding?'}
            message={
                many
                    ? `${finding.Code} — ${finding.Title}, on ${covers} colours. Your reason is recorded against each one, and they stay on the asset marked as overridden.`
                    : `${finding.Code} — ${finding.Title}. It stays on the asset, marked as overridden, with your name and reason against it.`
            }
            confirmText={saving ? 'Saving…' : many ? `Override all ${covers}` : 'Override'}
            cancelText="Cancel"
            loading={saving}
            confirmDisabled={trimmed.length === 0}
            onCancel={onCancel}
            onConfirm={() => onConfirm(trimmed)}
        >
            {/*
              The shared TextBox rather than a hand-rolled textarea: it already owns the label
              association, the character counter, the disabled and focus states and the error slot,
              and it is what every other field in the dashboard uses. A second implementation of a
              required multiline field is a second place for those behaviours to drift.
              Raised by Copilot on PR #137.
            */}
            <div className="mt-3 text-left">
                <TextBox
                    name="qa-override-reason"
                    label="Why should this not stand?"
                    required
                    multiline
                    rows={3}
                    value={reason}
                    onChange={setReason}
                    disabled={saving}
                    autoFocus
                    maxLength={REASON_MAX_LENGTH}
                    showCharCounter
                    placeholder="e.g. the speaker's title changed after the brief was written"
                    helperText={trimmed.length === 0 ? 'A reason is required.' : undefined}
                    error={error ?? undefined}
                />
            </div>
        </ConfirmationModal>
    )
}
